import { hHttpClient } from '@/plugins/ky';
import { applyTransforms, getTransforms, isAbsoluteUrl } from '@/utils/http-utils';
import { loadFiles } from '@/utils/load-files';
import { useDebounceFn } from '@vueuse/core';
import { KyResponse, Options, ResponsePromise, SearchParamsOption } from 'ky';
import { isUndefined, omitBy, isEmpty, isFunction } from 'lodash-es';
import queryString from 'query-string';
import { computed, inject, InjectionKey, reactive, readonly, ref, watch } from 'vue';
import { isPublicAuthPath } from '@/utils/public-auth-path';
import { isExpectedApiError } from '@/utils/expected-api-error';

type HttpMethod = NonNullable<Options['method']>;

/**
 * Per-attempt abort budget for background/polled HTTP (settings refresh, notification polls).
 * After this many milliseconds Ky throws TimeoutError — that is a failed request.
 * Background callers opt into `silentErrors`, so the failure is logged, not toasted.
 * 10s matches Ky's default, so polls are no more aggressive than interactive requests.
 * Note: this bounds a single attempt; Ky may still retry, so a persistently failing
 * poll can take a small multiple of this before it resolves — well under the 60s cadence.
 */
export const BACKGROUND_HTTP_TIMEOUT_MS = 10_000;

// TODO: Move
export interface LoginRedirectConfig {
  loginUrl: string;
  logoutUrl?: string;
}

export interface IHttpRequestConfig {
  apiBaseUrl: string;

  // If using API, this composable will automatically remove all occurrences of `onifyHubAPIPath` substring from the provided url in `fetch`
  onifyHubAPIPath?: string;

  // Application version for identification headers
  appVersion?: string;

  // Enable automatic redirect to login page on 401 responses (default: true)
  enableLoginRedirect?: boolean;

  // Login redirect configuration for accessing login URLs
  loginRedirectConfig?: LoginRedirectConfig;
}

export interface IHelixFetchOptions {
  immediate?: boolean;
  url: string | (() => string);
  params?: {
    [key: string]: any;
  };
  payload?: unknown;
  body?: BodyInit;
  isLive?: boolean;
  debounceTimeInMilliseconds?: number;
  method?: HttpMethod;
  precondition?: () => boolean;
  /**
   * Background/polled requests only. On failure, log to console instead of raising the
   * global error alert. Combine with `kyFetchOptions.timeout` (see `BACKGROUND_HTTP_TIMEOUT_MS`).
   */
  silentErrors?: boolean;
  /**
   * API error keys (`error.locale.key`) this request expects. Such an error raises no global
   * alert; it is exposed as `expectedError` (`{ key, message }`) until the next request.
   * Other errors still alert.
   */
  expectedErrorKeys?: string[];
}

/**
 * Options for a single `execute()` (aka `doFetch`) call. Extends Ky's `Options` with the
 * `silentErrors` convenience so per-call background silencing uses the same abstraction as
 * `IHelixFetchOptions.silentErrors` instead of poking Ky `context` directly at call sites.
 */
export type HFetchConfig = Options & { silentErrors?: boolean };

export interface IOnifyApiUploadFileOptions {
  type: 'private' | 'public';
  files: File[];
  ttl?: number;
  role?: string[];
  tag?: string[];
  refresh?: boolean;
}

export type HelixHttpRequestClient = ReturnType<typeof hCreateHttpRequestClient>;

export type HelixHttpClientUtilities = {
  updateHttpClientConfig: (config: Options) => void;
  updateApiClientConfig: (config: Options) => void;
  updateLoginRedirectConfig: (config: Partial<LoginRedirectConfig>) => void;
};

type LoginRedirectOutcome = 'redirected' | 'auth-path' | 'unavailable';

export const helixHttpRequestClientKey: InjectionKey<HelixHttpRequestClient> = Symbol('Helix Http Request Client');

/**
 * Transforms an object of search parameters into a URLSearchParams instance.
 *
 * @function
 * @name transformSearchParams
 * @param {Object} searchParamsObj - The object containing search parameters to be transformed.
 * @returns {URLSearchParams} - Returns a new URLSearchParams instance with the provided search parameters.
 */
function transformSearchParams(searchParamsObj: Object) {
  return new URLSearchParams(queryString.stringify(searchParamsObj));
}

/**
 * Generates the onify-app-name header value
 * @param appName - The application name
 * @param version - The application version
 * @param role - The application role (default: 'frontend')
 * @returns Formatted app name string
 */
function getOnifyAppName(appName: string, version: string, role: string = 'frontend'): string {
  return `${appName}; version=${version}; role=${role}`;
}

/**
 * Gets the encoded current URL origin for the onify-app-origin header
 * @returns Encoded URL or empty string if window is not available
 */
function getOnifyAppOrigin(): string {
  if (typeof window !== 'undefined') {
    return encodeURIComponent(window.location.href);
  }
  return '';
}

/**
 * Extracts only the path (without domain) from a full URL
 * @param url - The full URL to extract path from
 * @returns The path, search, and hash portion of the URL
 */
function extractUrlPath(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // Fallback to original URL if parsing fails
    return url;
  }
}

/**
 * A utility method that creates the Helix Http Request Client (built on `ky`) to be installed in the Vue app
 *
 * @function
 * @name hCreateHttpRequestClient
 * @description
 * This function provides methods to create and manage HTTP requests within a Vue application.
 * It includes functionalities to fetch data, update HTTP client configurations, and upload files.
 *
 * @param {IHttpRequestConfig} config - The configuration object for the HTTP request client.
 * @param {string} config.apiBaseUrl - The base URL for the API.
 * @param {string} [config.onifyHubAPIPath] - The optional path for the API.
 * @param {string} [config.appVersion] - The optional application version for identification headers.
 * @param {boolean} [config.enableLoginRedirect=true] - Enable automatic redirect to login page on 401 responses.
 * @param {Object} [config.loginRedirectConfig] - The optional login redirect configuration.
 *
 * @returns {Object} An object containing the following methods:
 * - `fetch`: A function to perform HTTP requests.
 * - `updateHttpClientConfig`: A function to update the HTTP client configuration.
 * - `updateApiClientConfig`: A function to update the API client configuration.
 * - `uploadFiles`: A function to upload files to the Onify API.
 */
export function hCreateHttpRequestClient({
  apiBaseUrl,
  onifyHubAPIPath,
  appVersion,
  enableLoginRedirect = true,
  loginRedirectConfig,
}: IHttpRequestConfig) {
  let httpClient = hHttpClient;
  let apiClient = hHttpClient.extend({});
  let currentLoginRedirectConfig = loginRedirectConfig;

  // Use provided appVersion or fallback to 'unknown'
  const version = appVersion ?? 'unknown';

  // Per-client flag — each hCreateHttpRequestClient call gets its own redirect state,
  // so multiple instances (e.g. in tests) don't interfere with each other.
  let isRedirecting = false;

  function redirectToLoginPage(): LoginRedirectOutcome {
    if (isRedirecting) return 'redirected';
    if (typeof window === 'undefined') return 'unavailable';
    // loginUrl is required — API owns error/internal login redirects.
    if (!currentLoginRedirectConfig || !currentLoginRedirectConfig.loginUrl) return 'unavailable';

    const { loginUrl, logoutUrl } = currentLoginRedirectConfig;
    const redirectPath = extractUrlPath(window.location.href);

    // Skip login/logout/error (isPublicAuthPath) so SSO failures on /error cannot
    // bounce back to loginUrl and restart the SSO loop. Hub also appends
    // `?completed=true` after SSO when `onify-redirect-on-completed` is set.
    const skipPaths = [logoutUrl, loginUrl];
    const fullUrl = window.location.pathname + window.location.search;
    const isAuthCompleted = new URLSearchParams(window.location.search).get('completed') === 'true';
    if (isPublicAuthPath(window.location.pathname) || isAuthCompleted || skipPaths.some((path) => path && fullUrl.includes(path))) {
      return 'auth-path';
    }

    isRedirecting = true;
    /*
      Redirect to the login URL on authentication failure.
      Includes a redirect param so the user returns to their original page after login.
      Error / internal login paths are API settings (error_url / internal_login_page) — not SPA query params.
    */
    window.location.href = `${loginUrl}?redirect=${encodeURIComponent(redirectPath)}`;

    return 'redirected';
  }

  apiClient = apiClient.extend({
    hooks: {
      beforeRequest: [
        ({ request }) => {
          request.headers.set('onify-app-name', getOnifyAppName('helix-app', version, 'frontend'));
          request.headers.set('onify-app-origin', getOnifyAppOrigin());
        },
      ],
      afterResponse: [
        async ({ response }) => {
          // Handle 401 unauthorized responses if redirect is enabled
          if (response?.status === 401 && enableLoginRedirect) {
            const redirectOutcome = redirectToLoginPage();
            // Swallow 401 only while navigating away. Auth pages (login/logout/error) must
            // receive the original 401 so the login form can show invalid credentials
            // instead of treating a synthetic 204 as success.
            if (redirectOutcome === 'redirected') {
              return new Response(null, { status: 204 });
            }
          }

          return response;
        },
      ],
    },
  });

  function fetch<T>(options: IHelixFetchOptions, kyFetchOptions?: Options) {
    const _options: IHelixFetchOptions = {
      immediate: true,
      debounceTimeInMilliseconds: 300,
      method: 'get',
      precondition: () => true,
      ...options,
    };

    const loading = ref(false);

    // Remove leading slashes and occurrences of `onifyHubAPIPath`
    const _onifyHubAPIPath = (onifyHubAPIPath ?? '').replace(/^\/+/g, '');

    const data = ref<T>();

    let initialFetch: Promise<KyResponse | undefined> | undefined;
    let lastResponse: KyResponse;

    const { transforms: queryTransforms, rawValues: rawQueryValues } = getTransforms(_options?.params ?? {});
    const { transforms: payloadTransforms } = getTransforms((_options?.payload as any) ?? {});

    const bodyRef = ref(_options?.body ?? null);
    const searchParams = reactive<{ [key: string]: any }>({ ...rawQueryValues });
    const payload = reactive((_options.payload as Object) ?? {});
    const expectedError = ref<{ key: string; message: string } | null>(null);

    async function doFetch(fetchConfig?: HFetchConfig) {
      if (_options.precondition && !_options.precondition()) {
        return;
      }

      expectedError.value = null;

      try {
        const actualUrl = isFunction(_options.url) ? _options.url() : _options.url;

        const _isAbsoluteUrl = isAbsoluteUrl(actualUrl);
        const _url = _isAbsoluteUrl ? actualUrl : actualUrl.replace(_onifyHubAPIPath, '').replace(/^\/+/g, '');

        loading.value = true;

        // `silentErrors` may come from the request definition or this specific execute() call.
        // Pull it off so it never leaks to Ky as an unknown option — it maps to `context` below.
        const { silentErrors: callSilentErrors, ...kyFetchConfig } = fetchConfig ?? {};
        const silentErrors = callSilentErrors ?? _options.silentErrors;

        const _fetchConfig: Options = {
          method: _options.method,
          ...kyFetchOptions,
          ...(!_isAbsoluteUrl && { prefix: apiBaseUrl }),
          ...(!!bodyRef.value && { body: bodyRef.value }),
          ...(!isEmpty(payload) && { json: applyTransforms(payload ?? {}, payloadTransforms) }),
          ...(!isEmpty(searchParams) && { searchParams: transformSearchParams(applyTransforms(searchParams, queryTransforms)) }),
        };

        // Extend modified config to preserve default config.
        const _mergedFetchConfig: Options = {
          ..._fetchConfig,
          ...kyFetchConfig,
        };

        // Map `silentErrors` to Ky 2 `context` (read by the ky proxy to log instead of toast),
        // preserving any caller-provided context from either the request or execute() options.
        const mergedContext = {
          ...(_fetchConfig.context as Record<string, unknown> | undefined),
          ...(kyFetchConfig.context as Record<string, unknown> | undefined),
          ...(silentErrors && { silentErrors: true }),
          ...(_options.expectedErrorKeys?.length && { expectedErrorKeys: _options.expectedErrorKeys }),
        };
        if (Object.keys(mergedContext).length > 0) {
          _mergedFetchConfig.context = mergedContext;
        }

        // Transform searchParams property of KyOptions object by removing undefined fields
        if (fetchConfig?.searchParams) {
          fetchConfig.searchParams = omitBy(_mergedFetchConfig?.searchParams as Object, isUndefined);
        }

        // Ky's `fetch` api syntax
        const client = _isAbsoluteUrl ? httpClient : apiClient;
        const res = await client(_url, { method: 'get', ..._mergedFetchConfig });

        // Update data ref
        if (res && res.status !== 204) {
          data.value = await res.clone().json();
        }

        lastResponse = res;
        loading.value = false;

        return res;
      } catch (error) {
        loading.value = false;
        if (isExpectedApiError(error)) {
          expectedError.value = { key: error.key, message: error.message };
        }
      }
    }

    const computedPrecondition = computed(_options.precondition!);

    if (_options?.immediate) {
      if (computedPrecondition.value) {
        initialFetch = doFetch();
      } else {
        const unwatchPrecondition = watch(computedPrecondition, (value) => {
          if (value) {
            initialFetch = doFetch();
            unwatchPrecondition();
          }
        });
      }
    }

    if (_options?.isLive) {
      // Debounce to give time (300 ms since their last key press) for the user to finish typing their search term before sending the request to the server
      const debounceFetch = useDebounceFn(async () => {
        await doFetch({
          ...(!!bodyRef.value && { body: bodyRef.value }),
          ...(!!_options?.payload && { json: applyTransforms(_options?.payload ?? {}, payloadTransforms) }),
          ...(!isEmpty(searchParams) && { searchParams: transformSearchParams(applyTransforms(searchParams, queryTransforms)) }),
        });
      }, _options.debounceTimeInMilliseconds);

      watch([searchParams, ...(payload ? [payload] : []), ...(bodyRef.value ? [bodyRef] : [])], () => {
        debounceFetch();
      });
    }

    async function response() {
      // If a request is already running or a response already exists, then get that otherwise make a request
      return lastResponse ? lastResponse : initialFetch != null ? initialFetch : doFetch();
    }

    return {
      data: readonly(data),
      execute: doFetch,
      response,
      params: searchParams,
      payload,
      body: bodyRef.value,
      loading,
      expectedError: readonly(expectedError),
      httpClient,
    };
  }

  function updateHttpClientConfig(config: Options) {
    httpClient = httpClient.extend(config);
  }

  function updateApiClientConfig(config: Options) {
    apiClient = apiClient.extend(config);
  }

  function updateLoginRedirectConfig(config: Partial<LoginRedirectConfig>) {
    currentLoginRedirectConfig = {
      ...(currentLoginRedirectConfig ?? { loginUrl: '' }),
      ...config,
    };
  }

  // Onify API specific upload method
  /**
   * Uploads multiple files to the specified API endpoint.
   *
   * @function
   * @name uploadFiles
   * @async
   * @param {IOnifyApiUploadFileOptions} options - The options for uploading files.
   * @param {File[]} options.files - The files to be uploaded.
   * @param {string} options.type - The type of files being uploaded.
   * @param {number} [options.ttl] - Time to live for the uploaded files.
   * @param {string} [options.role] - Role associated with the uploaded files.
   * @param {string} [options.tag] - Tag associated with the uploaded files.
   * @param {boolean} [options.refresh] - Whether to refresh the files.
   * @returns {Promise<Object[]>} - A promise that resolves to an array of responses from the API.
   * @description
   * This function uploads multiple files to a specified API endpoint. It first loads the files,
   * then constructs the search parameters from the provided options, and finally uploads each file
   * using the `httpClient` function.
   */
  async function uploadFiles(options: IOnifyApiUploadFileOptions) {
    const loadedFiles = await loadFiles(options.files);

    const { ttl, role, tag, refresh } = options;
    const searchParams: SearchParamsOption = queryString.stringify({ ttl, role, tag, refresh });

    return await Promise.all(
      loadedFiles.map(
        async ([fileName, fileContents]) =>
          await (
            await httpClient(`${apiBaseUrl}files/${options.type}/` + fileName, {
              searchParams,
              method: 'post',
              body: fileContents,
            })
          ).json(),
      ),
    );
  }

  fetch.updateHttpClientConfig = updateHttpClientConfig;
  fetch.uploadFiles = uploadFiles as unknown as (options: IOnifyApiUploadFileOptions) => ResponsePromise;
  fetch.updateApiClientConfig = updateApiClientConfig;
  fetch.updateLoginRedirectConfig = updateLoginRedirectConfig;

  return fetch;
}

/**
 * A Vue composable that provides an HTTP client instance.
 *
 * @function
 * @name useHFetch
 * @description
 * This composable retrieves an HTTP client instance using Vue's dependency injection.
 * It throws an error if the HTTP client is not initialized.
 *
 * @returns {Object} The HTTP client instance.
 * @throws {Error} Throws an error if the HTTP client is not initialized.
 */
export function useHFetch() {
  // Note that `inject()` is only accessible in a Vue component
  const apiClient = inject(helixHttpRequestClientKey);

  if (!apiClient) {
    throw new Error('Helix Http Client not initialized');
  }

  return apiClient;
}
